import React, { useEffect, useState } from "react";
import { Field } from "formik";
import { useTranslation } from "react-i18next";
import { getActiveCatalogue, getOperationalAreas, getProcesses, getSites } from "../../services/AiService";
import { AcesCatalogue, LovItem, Site } from "../../types/ai";
import { DateField } from "./DateField";

/** Checklist context used by QualiCheck AI (requirements.md §1): site, area, process, temperature range, shipment. */
export const ChecklistContextFields: React.FC<{ isTemplate: boolean }> = ({ isTemplate }) => {
  const { t } = useTranslation();
  const [sites, setSites] = useState<Site[]>([]);
  const [processes, setProcesses] = useState<LovItem[]>([]);
  const [areas, setAreas] = useState<LovItem[]>([]);
  useEffect(() => {
    getSites().then(setSites).catch(() => setSites([]));
    getProcesses().then(setProcesses).catch(() => setProcesses([]));
    getOperationalAreas().then(setAreas).catch(() => setAreas([]));
  }, []);

  return (
    <div className="row">
      <div className="col-lg-6">
        <label>{t("aces.ctx.sectionTitle")}</label>
        <div className="aces-muted">{t("aces.ctx.sectionHint")}</div>
      </div>
      <div className="col-lg-6">
        <div className="__inputField">
          <label>{t("aces.ctx.site")}</label>
          <Field as="select" name="context.siteId">
            <option value="">—</option>
            {sites.filter((s) => s.active).map((s) => <option key={s.id} value={s.id}>{s.name}{s.airportIata ? ` (${s.airportIata})` : ""}</option>)}
          </Field>
        </div>
        <div className="row">
          <div className="col-md-6 __inputField">
            <label>{t("aces.ctx.operationalArea")}</label>
            <Field name="context.operationalArea" list="ctx-areas" />
            <datalist id="ctx-areas">{areas.map((a) => <option key={a.value} value={a.value} />)}</datalist>
          </div>
          <div className="col-md-6 __inputField">
            <label>{t("aces.ctx.process")}</label>
            <Field name="context.process" list="ctx-processes" />
            <datalist id="ctx-processes">{processes.map((p) => <option key={p.value} value={p.value} />)}</datalist>
          </div>
        </div>
        <label className="aces-check" htmlFor="ctx-isTemperature">
          <Field type="checkbox" id="ctx-isTemperature" name="context.isTemperature" />
          {t("aces.ctx.isTemperature")}
        </label>
        <div className="row">
          <div className="col-md-6 __inputField">
            <label>{t("aces.ctx.temperatureMin")}</label>
            <Field type="number" step="0.1" name="context.temperatureMin" />
          </div>
          <div className="col-md-6 __inputField">
            <label>{t("aces.ctx.temperatureMax")}</label>
            <Field type="number" step="0.1" name="context.temperatureMax" />
          </div>
        </div>
        {!isTemplate && (
          <>
            <h5 className="mt-2">{t("aces.ctx.shipment")}</h5>
            <div className="row">
              <div className="col-md-6 __inputField"><label>{t("aces.ctx.awb")}</label><Field name="context.awb" /></div>
              <div className="col-md-6 __inputField"><label>{t("aces.ctx.flightNo")}</label><Field name="context.flightNo" /></div>
              <div className="col-md-6 __inputField"><label htmlFor="ctx-flightDate">{t("aces.ctx.flightDate")}</label><Field name="context.flightDate">
                {({ field, form }: any) => <DateField id="ctx-flightDate" className="" value={field.value} onChange={(v) => form.setFieldValue("context.flightDate", v)} />}
              </Field></div>
              <div className="col-md-6 __inputField"><label>{t("aces.ctx.shc")}</label><Field name="context.specialHandlingCodes" placeholder="PIL, CRT" /></div>
              <div className="col-md-6 __inputField"><label>{t("aces.ctx.origin")}</label><Field name="context.origin" maxLength={4} placeholder="IATA" /></div>
              <div className="col-md-6 __inputField"><label>{t("aces.ctx.destination")}</label><Field name="context.destination" maxLength={4} placeholder="IATA" /></div>
              <div className="col-12 __inputField"><label>{t("aces.ctx.cargo")}</label><Field name="context.cargoDescription" /></div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/** Optional per-question requirement text and standard clause mapping. */
export const QuestionControlFields: React.FC<{ prefix: string; catalogue: AcesCatalogue | null }> = ({ prefix, catalogue }) => {
  const { t } = useTranslation();
  return (
    <div className="row mt-2">
      <div className={catalogue ? "col-md-7" : "col-12"}>
        <Field name={`${prefix}.requirementText`} className="form-control" placeholder={t("aces.ctx.requirementPlaceholder")} />
      </div>
      {catalogue && (
        <div className="col-md-5">
          <Field as="select" name={`${prefix}.acesClauseId`} className="form-control">
            <option value="">{t("aces.ctx.noClause")}</option>
            {catalogue.clauses.map((c) => <option key={c.id} value={c.id}>{c.refCode} — {c.title}</option>)}
          </Field>
        </div>
      )}
    </div>
  );
};

export const useActiveCatalogue = () => {
  const [catalogue, setCatalogue] = useState<AcesCatalogue | null>(null);
  useEffect(() => {
    getActiveCatalogue().then((c) => setCatalogue(c && c.clauses.length ? c : null)).catch(() => setCatalogue(null));
  }, []);
  return catalogue;
};

export const initialContext = (d: any) => ({
  siteId: d?.site?.id ? String(d.site.id) : "",
  operationalArea: d?.operationalArea || "",
  process: d?.process || "",
  isTemperature: !!d?.isTemperature,
  temperatureMin: d?.temperatureMin ?? "",
  temperatureMax: d?.temperatureMax ?? "",
  awb: "",
  flightNo: "",
  flightDate: "",
  origin: "",
  destination: "",
  specialHandlingCodes: "",
  cargoDescription: "",
});
